import React, { useEffect, useState } from 'react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Button, Card, DataTable, Loader, PageHead, StatCard, Tabs, inr, num, titleCase,
} from '../components/ui';
import { reportApi } from '../api';
import { API_BASE, TOKEN_KEY } from '../api/client';
import { useToast } from '../context/ToastContext';

const TABS = [
  { value: 'sales', label: 'Sales' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'gst', label: 'GST summary' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'orders', label: 'Order register' },
  { value: 'companies', label: 'Companies' },
];

const isoDaysAgo = days => new Date(Date.now() - days * 864e5).toISOString().slice(0, 10);

const Reports = () => {
  const toast = useToast();
  const [tab, setTab] = useState('sales');
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { from, to };
    const call = {
      sales: reportApi.sales,
      revenue: reportApi.revenue,
      gst: reportApi.gst,
      inventory: reportApi.inventory,
      orders: reportApi.orders,
      companies: reportApi.companies,
    }[tab];

    call(params)
      .then(res => setData(res.data))
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, from, to]);

  /**
   * CSV lives behind the same auth as the JSON, so it is fetched with the
   * bearer token and handed to the browser as a blob rather than a plain link.
   */
  const downloadCsv = async () => {
    try {
      const url = `${API_BASE}/reports/${tab}?format=csv&from=${from}&to=${to}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
      if (!res.ok) throw new Error('Could not generate the CSV');

      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `tarpex-${tab}-${from}-to-${to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const tip = {
    contentStyle: { borderRadius: 10, border: '1px solid var(--line)', fontSize: 12.5, boxShadow: 'var(--shadow-md)' },
  };

  return (
    <>
      <PageHead
        title="Reports"
        subtitle="Sales, revenue, GST and inventory analytics — exportable to CSV."
        actions={
          <>
            <input type="date" className="input" style={{ width: 155 }} value={from} onChange={e => setFrom(e.target.value)} />
            <span className="text-muted">→</span>
            <input type="date" className="input" style={{ width: 155 }} value={to} onChange={e => setTo(e.target.value)} />
            <Button variant="soft" onClick={downloadCsv}>⤓ Export CSV</Button>
          </>
        }
      />

      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      {loading ? (
        <Loader label="Generating report…" />
      ) : !data ? null : (
        <>
          {tab === 'sales' && (
            <>
              <div className="stat-grid stagger mb-2">
                <StatCard accent label="Revenue" value={inr(data.totals.revenue, true)} icon="₹" foot={`${num(data.totals.orders)} orders`} />
                <StatCard label="Units sold" value={num(data.totals.unitsSold)} icon="▦" />
                <StatCard label="Average order value" value={inr(data.totals.averageOrderValue)} icon="◈" />
                <StatCard label="GST collected" value={inr(data.totals.tax, true)} icon="🧾" />
                <StatCard label="Platform commission" value={inr(data.totals.commission, true)} icon="✓" />
              </div>

              <Card title="Daily revenue" className="mb-2">
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={data.byDay} margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
                    <defs>
                      <linearGradient id="rep" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00A651" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#00A651" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false} minTickGap={30} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false} tickFormatter={v => inr(v, true)} width={62} />
                    <Tooltip {...tip} formatter={v => inr(v)} />
                    <Area type="monotone" dataKey="revenue" stroke="#00A651" strokeWidth={2.4} fill="url(#rep)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <div className="grid-2">
                <Card title="Top products" bodyClass="">
                  <DataTable
                    columns={[
                      { key: 'name', label: 'Product', render: r => <span className="cell-strong">{r.name}</span> },
                      { key: 'quantity', label: 'Qty', align: 'right', render: r => num(r.quantity) },
                      { key: 'revenue', label: 'Revenue', align: 'right', render: r => <strong>{inr(r.revenue)}</strong> },
                    ]}
                    rows={data.topProducts}
                  />
                </Card>
                <Card title="Top buyers" bodyClass="">
                  <DataTable
                    columns={[
                      { key: 'name', label: 'Company', render: r => <><div className="cell-strong">{r.name}</div><div className="cell-muted mono">{r.code}</div></> },
                      { key: 'orders', label: 'Orders', align: 'right', render: r => num(r.orders) },
                      { key: 'value', label: 'Value', align: 'right', render: r => <strong>{inr(r.value)}</strong> },
                    ]}
                    rows={data.topBuyers}
                  />
                </Card>
              </div>
            </>
          )}

          {tab === 'revenue' && (
            <>
              <div className="stat-grid stagger mb-2">
                <StatCard accent label="Collected" value={inr(data.collected, true)} icon="₹" foot={`${num(data.transactions)} transactions`} />
                <StatCard label="Outstanding" value={inr(data.outstanding, true)} icon="⏳" foot={`${num(data.outstandingOrders)} orders`} />
              </div>

              <div className="grid-2">
                <Card title="Collections by payment mode">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.byMode.map(m => ({ name: titleCase(m.mode), value: m.amount }))} margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} axisLine={false} tickLine={false} tickFormatter={v => inr(v, true)} width={62} />
                      <Tooltip {...tip} formatter={v => inr(v)} cursor={{ fill: 'var(--green-50)' }} />
                      <Bar dataKey="value" fill="#00A651" radius={[6, 6, 0, 0]} maxBarSize={48} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                <Card title="Monthly collections" bodyClass="">
                  <DataTable
                    columns={[
                      { key: 'month', label: 'Month' },
                      { key: 'amount', label: 'Collected', align: 'right', render: r => <strong>{inr(r.amount)}</strong> },
                    ]}
                    rows={data.byMonth}
                  />
                </Card>
              </div>
            </>
          )}

          {tab === 'gst' && (
            <>
              <div className="stat-grid stagger mb-2">
                <StatCard accent label="Taxable value" value={inr(data.totals.taxableValue, true)} icon="🧾" />
                <StatCard label="Total tax" value={inr(data.totals.totalTax, true)} icon="₹" />
              </div>
              <DataTable
                columns={[
                  { key: 'hsn', label: 'HSN', render: r => <span className="mono cell-strong">{r.hsn}</span> },
                  { key: 'gstRate', label: 'Rate', align: 'center', render: r => `${r.gstRate}%` },
                  { key: 'quantity', label: 'Qty', align: 'right', render: r => num(r.quantity) },
                  { key: 'taxableValue', label: 'Taxable value', align: 'right', render: r => inr(r.taxableValue) },
                  { key: 'cgst', label: 'CGST', align: 'right', render: r => inr(r.cgst) },
                  { key: 'sgst', label: 'SGST', align: 'right', render: r => inr(r.sgst) },
                  { key: 'igst', label: 'IGST', align: 'right', render: r => inr(r.igst) },
                  { key: 'totalTax', label: 'Total tax', align: 'right', render: r => <strong>{inr(r.totalTax)}</strong> },
                ]}
                rows={data.rows}
              />
            </>
          )}

          {tab === 'inventory' && (
            <>
              <div className="stat-grid stagger mb-2">
                <StatCard accent label="Stock value" value={inr(data.totals.stockValue, true)} icon="⛁" foot={`${num(data.totals.skus)} rows`} />
                <StatCard label="Units on hand" value={num(data.totals.quantity)} icon="▦" />
                <StatCard label="Available" value={num(data.totals.available)} icon="✓" />
                <StatCard label="Low stock" value={num(data.totals.lowStock)} icon="⚠" />
              </div>
              <DataTable
                columns={[
                  { key: 'product', label: 'Product', render: r => <><div className="cell-strong">{r.product}</div><div className="cell-muted">{r.sku}</div></> },
                  { key: 'warehouse', label: 'Warehouse', render: r => <><div>{r.warehouse}</div><div className="cell-muted">{r.city}</div></> },
                  { key: 'quantity', label: 'On hand', align: 'right', render: r => num(r.quantity) },
                  { key: 'reserved', label: 'Reserved', align: 'right', render: r => num(r.reserved) },
                  { key: 'available', label: 'Available', align: 'right', render: r => <strong>{num(r.available)}</strong> },
                  { key: 'stockValue', label: 'Value', align: 'right', render: r => inr(r.stockValue) },
                ]}
                rows={data.rows}
              />
            </>
          )}

          {tab === 'orders' && (
            <>
              <p className="text-muted mb-2">{num(data.count)} orders between {from} and {to}.</p>
              <DataTable
                columns={[
                  { key: 'orderNumber', label: 'Order', render: r => <span className="mono cell-strong">{r.orderNumber}</span> },
                  { key: 'date', label: 'Date' },
                  { key: 'buyer', label: 'Buyer' },
                  { key: 'seller', label: 'Seller' },
                  { key: 'quantity', label: 'Qty', align: 'right', render: r => num(r.quantity) },
                  { key: 'subtotal', label: 'Taxable', align: 'right', render: r => inr(r.subtotal) },
                  { key: 'gst', label: 'GST', align: 'right', render: r => inr(r.gst) },
                  { key: 'grandTotal', label: 'Total', align: 'right', render: r => <strong>{inr(r.grandTotal)}</strong> },
                  { key: 'status', label: 'Status', render: r => titleCase(r.status) },
                  { key: 'invoice', label: 'Invoice', render: r => <span className="mono text-sm">{r.invoice || '—'}</span> },
                ]}
                rows={data.rows}
              />
            </>
          )}

          {tab === 'companies' && (
            <>
              <p className="text-muted mb-2">{num(data.count)} companies registered.</p>
              <DataTable
                columns={[
                  { key: 'name', label: 'Company', render: r => <><div className="cell-strong">{r.name}</div><div className="cell-muted mono">{r.code}</div></> },
                  { key: 'type', label: 'Type', render: r => titleCase(r.type) },
                  { key: 'gstNumber', label: 'GSTIN', render: r => <span className="mono text-sm">{r.gstNumber || '—'}</span> },
                  { key: 'city', label: 'City', render: r => <>{r.city}{r.state ? <div className="cell-muted">{r.state}</div> : null}</> },
                  { key: 'status', label: 'KYC', render: r => titleCase(r.status) },
                  { key: 'totalOrders', label: 'Orders', align: 'right', render: r => num(r.totalOrders) },
                  { key: 'totalBusiness', label: 'Business', align: 'right', render: r => inr(r.totalBusiness) },
                  { key: 'registeredOn', label: 'Registered' },
                ]}
                rows={data.rows}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default Reports;
